// Questions data for the AI Org Health Score assessment
// 6 dimensions, 20 questions total

export const DIMENSIONS = [
  {
    id: 'strategic',
    name: 'Strategic Clarity',
    icon: 'Target',
    color: '#6c5ce7',
    description: 'How well-aligned is leadership around priorities, and can the org translate strategy into daily execution?'
  },
  {
    id: 'process',
    name: 'Process Efficiency',
    icon: 'Cog',
    color: '#00d2a0',
    description: 'How streamlined are core workflows? Where does manual work, duplication, or bottlenecks cost you time?'
  },
  {
    id: 'data',
    name: 'Data & Decision Flow',
    icon: 'BarChart3',
    color: '#f7b731',
    description: 'Can leadership access the data they need, when they need it, to make confident decisions?'
  },
  {
    id: 'tech',
    name: 'Tech Stack Cohesion',
    icon: 'Layers',
    color: '#a29bfe',
    description: 'Are your tools integrated, or do teams live in disconnected silos with manual handoffs?'
  },
  {
    id: 'people',
    name: 'Team & Capacity',
    icon: 'Users',
    color: '#fd79a8',
    description: 'Is your team spending time on high-value work, or drowning in admin, reporting, and firefighting?'
  },
  {
    id: 'ai',
    name: 'AI Readiness',
    icon: 'Sparkles',
    color: '#00cec9',
    description: 'How prepared is your organisation to adopt AI — culturally, structurally, and technically?'
  }
];

export const QUESTIONS = [
  // Strategic Clarity (3 questions)
  {
    id: 1,
    dimensionId: 'strategic',
    text: "How clearly can your mid-level managers articulate the company's top 3 priorities for this quarter?",
    options: [
      { text: "They could recite them in their sleep", score: 5 },
      { text: "They broadly know the direction, but details are fuzzy", score: 3 },
      { text: "Each team seems to have different priorities", score: 2 },
      { text: "We don't really have documented priorities", score: 1 }
    ]
  },
  {
    id: 2,
    dimensionId: 'strategic',
    text: "When a new initiative is proposed, how quickly can you assess whether it aligns with strategy?",
    options: [
      { text: "We have a clear framework — takes minutes", score: 5 },
      { text: "We discuss it but it takes a few meetings", score: 3 },
      { text: "It usually depends on who champions it", score: 2 },
      { text: "Most things get approved if someone is passionate enough", score: 1 }
    ]
  },
  {
    id: 3,
    dimensionId: 'strategic',
    text: "How often do operational goals cascade cleanly from board-level OKRs to individual team targets?",
    options: [
      { text: "Always — there's a clear line from top to bottom", score: 5 },
      { text: "Mostly — some teams are better than others", score: 3 },
      { text: "Rarely — teams set their own goals", score: 2 },
      { text: "We don't use a formal goal-setting framework", score: 1 }
    ]
  },
  // Process Efficiency (4 questions)
  {
    id: 4,
    dimensionId: 'process',
    text: "What percentage of your team's weekly time goes into manual, repetitive tasks (data entry, status updates, report formatting)?",
    options: [
      { text: "Less than 10% — we've automated the boring stuff", score: 5 },
      { text: "10-25% — some repetition but manageable", score: 3 },
      { text: "25-40% — a significant time drain", score: 2 },
      { text: "Over 40% — we're drowning in admin", score: 1 }
    ]
  },
  {
    id: 5,
    dimensionId: 'process',
    text: "When a core process breaks or someone leaves, how quickly can the team recover?",
    options: [
      { text: "Immediately — processes are documented and transferable", score: 5 },
      { text: "Within a week — some knowledge transfer needed", score: 3 },
      { text: "It's painful — key knowledge lives in people's heads", score: 2 },
      { text: "We've lost critical capability when people left", score: 1 }
    ]
  },
  {
    id: 6,
    dimensionId: 'process',
    text: "How many steps does your most common cross-team handoff require?",
    options: [
      { text: "1-2 steps, mostly automated", score: 5 },
      { text: "3-5 steps with some manual coordination", score: 3 },
      { text: "5+ steps involving emails, spreadsheets, and chasing", score: 2 },
      { text: "It's different every time — no standard process", score: 1 }
    ]
  },
  {
    id: 7,
    dimensionId: 'process',
    text: "How would you describe your approach to process improvement?",
    options: [
      { text: "Continuous — we review and optimise quarterly", score: 5 },
      { text: "Reactive — we fix things when they break", score: 3 },
      { text: "Ad hoc — improvement happens when someone complains", score: 2 },
      { text: "We haven't mapped most of our processes", score: 1 }
    ]
  },
  // Data & Decision Flow (3 questions)
  {
    id: 8,
    dimensionId: 'data',
    text: "If the CEO asks 'how did last month perform?', how long does it take to get a confident answer?",
    options: [
      { text: "Seconds — it's on a live dashboard", score: 5 },
      { text: "A few hours — we need to pull from multiple sources", score: 3 },
      { text: "A day or more — someone has to build a spreadsheet", score: 2 },
      { text: "We'd be guessing, honestly", score: 1 }
    ]
  },
  {
    id: 9,
    dimensionId: 'data',
    text: "How often do teams make decisions based on conflicting or outdated data?",
    options: [
      { text: "Never — we have a single source of truth", score: 5 },
      { text: "Occasionally — minor version-control issues", score: 3 },
      { text: "Frequently — different teams have different numbers", score: 2 },
      { text: "Constantly — we joke about 'which spreadsheet' to trust", score: 1 }
    ]
  },
  {
    id: 10,
    dimensionId: 'data',
    text: "Can you track a customer or project's journey end-to-end across all touchpoints?",
    options: [
      { text: "Yes — fully integrated view from first touch to delivery", score: 5 },
      { text: "Mostly — a few gaps between departments", score: 3 },
      { text: "Partially — we see fragments in different systems", score: 2 },
      { text: "No — each team has their own isolated view", score: 1 }
    ]
  },
  // Tech Stack Cohesion (3 questions)
  {
    id: 11,
    dimensionId: 'tech',
    text: "How many of your core business tools talk to each other without manual intervention?",
    options: [
      { text: "Almost all — our stack is integrated", score: 5 },
      { text: "About half — some key integrations work", score: 3 },
      { text: "Very few — we copy-paste between systems", score: 2 },
      { text: "None — every tool is an island", score: 1 }
    ]
  },
  {
    id: 12,
    dimensionId: 'tech',
    text: "How would you describe your current tech stack?",
    options: [
      { text: "Lean and intentional — every tool earns its place", score: 5 },
      { text: "Functional but growing — some overlap emerging", score: 3 },
      { text: "Bloated — we have tools nobody uses", score: 2 },
      { text: "Chaotic — nobody knows what we're paying for", score: 1 }
    ]
  },
  {
    id: 13,
    dimensionId: 'tech',
    text: "When you onboard a new team member, how long until they're productive with your tools?",
    options: [
      { text: "Days — the stack is intuitive and documented", score: 5 },
      { text: "1-2 weeks — some training required", score: 3 },
      { text: "3-4 weeks — it's a steep learning curve", score: 2 },
      { text: "Months — they need a sherpa", score: 1 }
    ]
  },
  // Team & Capacity (3 questions)
  {
    id: 14,
    dimensionId: 'people',
    text: "What's the ratio of high-value work (strategy, client delivery, innovation) to operational overhead for your senior team?",
    options: [
      { text: "80/20 — they mostly do what they're hired for", score: 5 },
      { text: "60/40 — decent but too many status meetings", score: 3 },
      { text: "40/60 — more firefighting than building", score: 2 },
      { text: "20/80 — they're expensive administrators", score: 1 }
    ]
  },
  {
    id: 15,
    dimensionId: 'people',
    text: "How often does your team hit capacity ceilings — needing to hire just to maintain current output?",
    options: [
      { text: "Rarely — we scale through systems, not headcount", score: 5 },
      { text: "Sometimes — growth triggers hiring", score: 3 },
      { text: "Often — we're always 'one person short'", score: 2 },
      { text: "Constantly — we can't keep up", score: 1 }
    ]
  },
  {
    id: 16,
    dimensionId: 'people',
    text: "When something urgent comes in, what gets sacrificed?",
    options: [
      { text: "Nothing major — we have buffer and clear escalation paths", score: 5 },
      { text: "Non-critical projects get delayed", score: 3 },
      { text: "Important-but-not-urgent work — always", score: 2 },
      { text: "People's evenings and weekends", score: 1 }
    ]
  },
  // AI Readiness (4 questions)
  {
    id: 17,
    dimensionId: 'ai',
    text: "Has your organisation experimented with AI or automation in any operational process?",
    options: [
      { text: "Yes — we have AI in production driving results", score: 5 },
      { text: "Yes — we've run pilots or proofs of concept", score: 3 },
      { text: "We've discussed it but haven't started", score: 2 },
      { text: "AI isn't on our radar yet", score: 1 }
    ]
  },
  {
    id: 18,
    dimensionId: 'ai',
    text: "How would you describe your organisation's data hygiene?",
    options: [
      { text: "Clean, structured, and accessible — AI-ready", score: 5 },
      { text: "Decent — some cleaning needed but fundamentals are there", score: 3 },
      { text: "Messy — scattered across spreadsheets and inboxes", score: 2 },
      { text: "We don't really capture operational data systematically", score: 1 }
    ]
  },
  {
    id: 19,
    dimensionId: 'ai',
    text: "If you could automate one thing tomorrow, how clearly could you define what it is?",
    options: [
      { text: "Crystal clear — we have a prioritised list", score: 5 },
      { text: "We know the pain points but haven't scoped solutions", score: 3 },
      { text: "Vaguely — 'everything takes too long'", score: 2 },
      { text: "We wouldn't know where to start", score: 1 }
    ]
  },
  {
    id: 20,
    dimensionId: 'ai',
    text: "How does your leadership team view AI adoption?",
    options: [
      { text: "It's a strategic priority with budget and ownership", score: 5 },
      { text: "Interested but no formal plan yet", score: 3 },
      { text: "Sceptical — waiting to see proof", score: 2 },
      { text: "Haven't discussed it seriously", score: 1 }
    ]
  }
];

// Get dimension by ID
export const getDimension = (id) => DIMENSIONS.find(d => d.id === id);

// Get questions by dimension
export const getQuestionsByDimension = (dimensionId) => 
  QUESTIONS.filter(q => q.dimensionId === dimensionId);

// Indices where micro-insights should appear (after these questions)
export const MICRO_INSIGHT_INDICES = [4, 8, 11, 14, 18];

// Question distribution per dimension (for reference)
export const DIMENSION_QUESTION_COUNT = {
  strategic: 3,  // Questions 1-3
  process: 4,    // Questions 4-7
  data: 3,       // Questions 8-10
  tech: 3,       // Questions 11-13
  people: 3,     // Questions 14-16
  ai: 4          // Questions 17-20
};

// Scoring note: Each dimension is normalized to 0-100%
// regardless of question count, ensuring equal weight in overall score
